import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useAccess } from "./access";
import { useBusiness } from "./business";
import { Sparkline } from "./charts";

/**
 * Home's first view, before a conversation: the last 30 days at a glance along the top (when Analytics is on), and
 * the greeting with the ask box in the middle of the page.
 */

interface Overview {
  totals: { sessions: number };
  daily: { date: string; sessions: number }[];
  shop?: { currency: string; totals: { orders: number; sales: number }; days: { date: string; sales: number; orders: number }[] } | null;
  registrations?: { total: number; days: { date: string; registrations?: number; n?: number }[] } | null;
}

const num = (n: number) => n.toLocaleString("en-AU");

export function HomeMetrics() {
  const { has, data } = useBusiness();
  const { can } = useAccess();
  const on = has("analytics") && can("analytics.view");
  const q = useQuery({ queryKey: ["home", "metrics"], queryFn: () => api<Overview>("/analytics/overview?days=30"), enabled: on, staleTime: 300_000, retry: false });
  if (!on || !q.data) return null;
  const d = q.data;
  const currency = d.shop?.currency ?? data.business.currency;
  const money = (cents: number) => new Intl.NumberFormat(data.business.locale || "en-AU", { style: "currency", currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  const sessions = d.totals.sessions;
  const orders = d.shop?.totals.orders ?? 0;
  const items: { label: string; value: string; spark: number[] }[] = [
    { label: "Sessions", value: num(sessions), spark: d.daily.map((x) => x.sessions) },
  ];
  if (d.shop) {
    items.push({ label: "Total sales", value: money(d.shop.totals.sales), spark: d.shop.days.map((x) => x.sales) });
    items.push({ label: "Orders", value: num(orders), spark: d.shop.days.map((x) => x.orders) });
    items.push({ label: "Conversion rate", value: `${sessions ? ((orders / sessions) * 100).toFixed(1) : "0"}%`, spark: [] });
  }
  if (d.registrations) items.push({ label: "Registrations", value: num(d.registrations.total), spark: d.registrations.days.map((x) => x.registrations ?? x.n ?? 0) });
  return (
    <div className="home-metrics">
      <span className="home-metrics__range">
        <small>All channels</small>
        <b>Last 30 days</b>
      </span>
      <Link to="/analytics" className="home-metrics__card" aria-label="The last 30 days in Analytics">
        {items.map((m) => (
          <span key={m.label} className="home-metric">
            <small>{m.label}</small>
            <span className="home-metric__row">
              <b>{m.value}</b>
              {m.spark.length > 1 && <Sparkline values={m.spark} label={`${m.label} each day`} />}
            </span>
          </span>
        ))}
      </Link>
    </div>
  );
}
